import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Autocomplete, useLoadScript } from "@react-google-maps/api";
import {
  X,
  Plus,
  Trash2,
  Phone,
  Mail,
  MapPin,
  Briefcase,
  Cake,
  Home,
  Heart,
  FileText,
  Star,
  Tag,
  Check,
} from "lucide-react";
import { toast } from "react-toastify";
import { useStateValue } from "../../context/StateProvider";
import {
  loadContactGroupsFromFirestore,
  createContactGroup,
} from "../../utils/contactGroups";

const PHONE_TYPES = ["mobile", "home", "work", "other"];
const EMAIL_TYPES = ["personal", "work", "other"];
// Same key + libraries as the rest of the app — the Maps loader requires
// identical options everywhere it's used.
const GMAPS_LIBRARIES = ["places"];

const blankContact = {
  firstName: "",
  lastName: "",
  nickname: "",
  phones: [],
  emails: [],
  address: { street: "", city: "", state: "", postalCode: "", country: "" },
  organization: { company: "", title: "" },
  birthday: "",
  homeAnniversary: "",
  spouse: "",
  notes: "",
  labels: [],
};

function Section({ icon: Icon, title, children }) {
  return (
    <div className="mb-5">
      <h3 className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-slate-500 mb-2">
        <Icon size={14} />
        {title}
      </h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-slate-600 mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}

const inputClass =
  "w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500";

export default function ContactEditDrawer({
  initial,
  onClose,
  onSubmit,
  saving = false,
}) {
  const [{ user }] = useStateValue();
  const isEdit = Boolean(initial?.id);
  const [form, setForm] = useState(() => ({
    ...blankContact,
    ...(initial || {}),
    address: { ...blankContact.address, ...(initial?.address || {}) },
    organization: {
      ...blankContact.organization,
      ...(initial?.organization || {}),
    },
    phones: initial?.phones?.length
      ? [...initial.phones]
      : [{ value: "", type: "mobile", primary: true }],
    emails: initial?.emails?.length
      ? [...initial.emails]
      : [{ value: "", type: "personal", primary: true }],
    labels: Array.isArray(initial?.labels) ? [...initial.labels] : [],
  }));

  // All available label groups (loaded from Firestore once on open).
  // Empty until the backend syncs from Google.
  const [availableGroups, setAvailableGroups] = useState([]);
  const [labelPickerOpen, setLabelPickerOpen] = useState(false);
  const [labelCreateName, setLabelCreateName] = useState("");
  const [creatingLabel, setCreatingLabel] = useState(false);
  const [labelSearch, setLabelSearch] = useState("");

  // Google Places on the Street field — picking a suggestion fills street,
  // city, state, and ZIP in one go (same lookup as the deal/lead forms).
  const { isLoaded: placesReady } = useLoadScript({
    googleMapsApiKey: "AIzaSyClMaFJLo3buNYK-siytTa6AWD_72TgrE8",
    libraries: GMAPS_LIBRARIES,
  });
  const streetAcRef = useRef(null);
  const onStreetPicked = () => {
    const place = streetAcRef.current?.getPlace();
    if (!place) return;
    const comps = place.address_components || [];
    const get = (type, short = false) => {
      const c = comps.find((x) => x.types.includes(type));
      return c ? (short ? c.short_name : c.long_name) : "";
    };
    const street = [get("street_number"), get("route")]
      .filter(Boolean)
      .join(" ");
    setForm((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        street: street || place.formatted_address || prev.address.street,
        city: get("locality") || get("sublocality") || prev.address.city,
        state: get("administrative_area_level_1", true) || prev.address.state,
        postalCode: get("postal_code") || prev.address.postalCode,
      },
    }));
  };

  // Lock body scroll while drawer is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Load label groups (cached in Firestore by the backend's
  // /list-contact-groups). Empty array until Humza's endpoints ship.
  useEffect(() => {
    let cancelled = false;
    loadContactGroupsFromFirestore(user?.id).then((groups) => {
      if (!cancelled) setAvailableGroups(groups);
    });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const labelIsAttached = (group) =>
    (form.labels || []).some(
      (l) => l.groupResourceName === group.resourceName,
    );

  const attachLabel = (group) => {
    setForm((prev) => ({
      ...prev,
      labels: [
        ...(prev.labels || []),
        { groupResourceName: group.resourceName, name: group.name },
      ],
    }));
    setLabelPickerOpen(false);
  };

  const removeLabel = (resourceName) => {
    setForm((prev) => ({
      ...prev,
      labels: (prev.labels || []).filter(
        (l) => l.groupResourceName !== resourceName,
      ),
    }));
  };

  const handleCreateLabel = async () => {
    const name = labelCreateName.trim();
    if (!name) return;
    setCreatingLabel(true);
    try {
      const created = await createContactGroup(user?.id, name);
      const next = {
        resourceName: created.resourceName,
        name: created.name,
        source: "user",
      };
      setAvailableGroups((prev) => [...prev, next]);
      attachLabel(next);
      setLabelCreateName("");
      toast.success("Label created.");
    } catch (err) {
      console.error("Create label failed:", err);
      toast.info(
        "Labels backend not deployed yet — your label will sync once Humza ships.",
      );
    } finally {
      setCreatingLabel(false);
    }
  };

  const updateField = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const updateAddress = (key, value) =>
    setForm((prev) => ({
      ...prev,
      address: { ...prev.address, [key]: value },
    }));

  const updateOrg = (key, value) =>
    setForm((prev) => ({
      ...prev,
      organization: { ...prev.organization, [key]: value },
    }));

  // -------- Phone helpers --------
  const addPhone = () =>
    setForm((prev) => ({
      ...prev,
      phones: [
        ...prev.phones,
        {
          value: "",
          type: "mobile",
          primary: prev.phones.length === 0,
        },
      ],
    }));

  const updatePhone = (i, patch) =>
    setForm((prev) => ({
      ...prev,
      phones: prev.phones.map((p, idx) => (idx === i ? { ...p, ...patch } : p)),
    }));

  const removePhone = (i) =>
    setForm((prev) => {
      const next = prev.phones.filter((_, idx) => idx !== i);
      // If we removed the primary, promote the first remaining to primary.
      if (next.length > 0 && !next.some((p) => p.primary)) {
        next[0] = { ...next[0], primary: true };
      }
      return { ...prev, phones: next };
    });

  const setPrimaryPhone = (i) =>
    setForm((prev) => ({
      ...prev,
      phones: prev.phones.map((p, idx) => ({ ...p, primary: idx === i })),
    }));

  // -------- Email helpers --------
  const addEmail = () =>
    setForm((prev) => ({
      ...prev,
      emails: [
        ...prev.emails,
        {
          value: "",
          type: "personal",
          primary: prev.emails.length === 0,
        },
      ],
    }));

  const updateEmail = (i, patch) =>
    setForm((prev) => ({
      ...prev,
      emails: prev.emails.map((e, idx) => (idx === i ? { ...e, ...patch } : e)),
    }));

  const removeEmail = (i) =>
    setForm((prev) => {
      const next = prev.emails.filter((_, idx) => idx !== i);
      if (next.length > 0 && !next.some((e) => e.primary)) {
        next[0] = { ...next[0], primary: true };
      }
      return { ...prev, emails: next };
    });

  const setPrimaryEmail = (i) =>
    setForm((prev) => ({
      ...prev,
      emails: prev.emails.map((e, idx) => ({ ...e, primary: idx === i })),
    }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.firstName.trim() && !form.lastName.trim()) {
      toast.error("Please enter a name");
      return;
    }
    onSubmit?.(form);
  };

  return createPortal(
    <>
      <div
        className="fixed inset-0 bg-black/50 z-[100]"
        onClick={saving ? undefined : onClose}
      />
      <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 pointer-events-none">
        <div className="bg-white shadow-2xl rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden pointer-events-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-slate-900 text-white shrink-0">
          <h2 className="text-base font-semibold">
            {isEdit ? "Edit contact" : "New contact"}
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="w-8 h-8 bg-white/15 hover:bg-white/25 rounded-full flex items-center justify-center disabled:opacity-50"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Form body */}
        <form
          onSubmit={handleSubmit}
          className="flex-1 flex flex-col min-h-0"
        >
          <div className="flex-1 overflow-y-auto px-5 py-4">
            <Section icon={FileText} title="Identity">
              <div className="grid grid-cols-2 gap-3">
                <Field label="First Name">
                  <input
                    className={inputClass}
                    value={form.firstName}
                    onChange={(e) => updateField("firstName", e.target.value)}
                    autoFocus
                  />
                </Field>
                <Field label="Last Name">
                  <input
                    className={inputClass}
                    value={form.lastName}
                    onChange={(e) => updateField("lastName", e.target.value)}
                  />
                </Field>
              </div>
              <Field label="Nickname (Optional)">
                <input
                  className={inputClass}
                  value={form.nickname}
                  onChange={(e) => updateField("nickname", e.target.value)}
                />
              </Field>
            </Section>

            <Section icon={Phone} title="Phone">
              {form.phones.map((p, i) => (
                <div key={i} className="flex items-center gap-2">
                  <select
                    className="border border-slate-300 rounded-md px-2 py-2 text-xs capitalize bg-white"
                    value={p.type}
                    onChange={(e) => updatePhone(i, { type: e.target.value })}
                  >
                    {PHONE_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                  <input
                    className={`${inputClass} flex-1`}
                    type="tel"
                    value={p.value}
                    onChange={(e) => updatePhone(i, { value: e.target.value })}
                    placeholder="Phone number"
                  />
                  <button
                    type="button"
                    onClick={() => setPrimaryPhone(i)}
                    title={p.primary ? "Primary" : "Make primary"}
                    className={`p-1.5 rounded ${p.primary ? "text-amber-500" : "text-slate-300 hover:text-slate-500"}`}
                  >
                    <Star size={16} fill={p.primary ? "currentColor" : "none"} />
                  </button>
                  <button
                    type="button"
                    onClick={() => removePhone(i)}
                    className="p-1.5 rounded text-slate-400 hover:text-red-500"
                    aria-label="Remove phone"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addPhone}
                className="inline-flex items-center gap-1 text-xs font-semibold text-slate-800 hover:text-slate-900"
              >
                <Plus size={12} />
                Add phone
              </button>
            </Section>

            <Section icon={Mail} title="Email">
              {form.emails.map((em, i) => (
                <div key={i} className="flex items-center gap-2">
                  <select
                    className="border border-slate-300 rounded-md px-2 py-2 text-xs capitalize bg-white"
                    value={em.type}
                    onChange={(e) => updateEmail(i, { type: e.target.value })}
                  >
                    {EMAIL_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                  <input
                    className={`${inputClass} flex-1`}
                    type="email"
                    value={em.value}
                    onChange={(e) => updateEmail(i, { value: e.target.value })}
                    placeholder="email@example.com"
                  />
                  <button
                    type="button"
                    onClick={() => setPrimaryEmail(i)}
                    title={em.primary ? "Primary" : "Make primary"}
                    className={`p-1.5 rounded ${em.primary ? "text-amber-500" : "text-slate-300 hover:text-slate-500"}`}
                  >
                    <Star size={16} fill={em.primary ? "currentColor" : "none"} />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeEmail(i)}
                    className="p-1.5 rounded text-slate-400 hover:text-red-500"
                    aria-label="Remove email"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addEmail}
                className="inline-flex items-center gap-1 text-xs font-semibold text-slate-800 hover:text-slate-900"
              >
                <Plus size={12} />
                Add email
              </button>
            </Section>

            <Section icon={MapPin} title="Address">
              <Field label="Street">
                {placesReady ? (
                  <Autocomplete
                    onLoad={(ref) => (streetAcRef.current = ref)}
                    onPlaceChanged={onStreetPicked}
                  >
                    <input
                      className={inputClass}
                      value={form.address.street}
                      onChange={(e) => updateAddress("street", e.target.value)}
                      placeholder="Start typing an address…"
                    />
                  </Autocomplete>
                ) : (
                  <input
                    className={inputClass}
                    value={form.address.street}
                    onChange={(e) => updateAddress("street", e.target.value)}
                  />
                )}
              </Field>
              <div className="grid grid-cols-6 gap-2">
                <div className="col-span-3">
                  <Field label="City">
                    <input
                      className={inputClass}
                      value={form.address.city}
                      onChange={(e) => updateAddress("city", e.target.value)}
                    />
                  </Field>
                </div>
                <div className="col-span-1">
                  <Field label="State">
                    <input
                      className={inputClass}
                      value={form.address.state}
                      onChange={(e) => updateAddress("state", e.target.value)}
                    />
                  </Field>
                </div>
                <div className="col-span-2">
                  <Field label="ZIP">
                    <input
                      className={inputClass}
                      value={form.address.postalCode}
                      onChange={(e) =>
                        updateAddress("postalCode", e.target.value)
                      }
                    />
                  </Field>
                </div>
              </div>
            </Section>

            <Section icon={Briefcase} title="Organization">
              <Field label="Company">
                <input
                  className={inputClass}
                  value={form.organization.company}
                  onChange={(e) => updateOrg("company", e.target.value)}
                />
              </Field>
              <Field label="Title">
                <input
                  className={inputClass}
                  value={form.organization.title}
                  onChange={(e) => updateOrg("title", e.target.value)}
                />
              </Field>
            </Section>

            <Section icon={Cake} title="Personal">
              <Field label="Birthday">
                <input
                  className={inputClass}
                  type="date"
                  value={form.birthday}
                  onChange={(e) => updateField("birthday", e.target.value)}
                />
              </Field>
              <Field label="Home Anniversary">
                <input
                  className={inputClass}
                  type="date"
                  value={form.homeAnniversary}
                  onChange={(e) =>
                    updateField("homeAnniversary", e.target.value)
                  }
                />
              </Field>
              <Field label="Spouse / Partner">
                <input
                  className={inputClass}
                  value={form.spouse}
                  onChange={(e) => updateField("spouse", e.target.value)}
                  placeholder="Name"
                />
              </Field>
            </Section>

            <Section icon={Tag} title="Labels">
              <div className="flex flex-wrap items-center gap-1.5">
                {(form.labels || []).length === 0 && (
                  <span className="text-xs text-slate-400">
                    No labels yet
                  </span>
                )}
                {(form.labels || []).map((l) => (
                  <span
                    key={l.groupResourceName}
                    className="inline-flex items-center gap-1 bg-slate-100 text-slate-800 text-xs font-medium px-2 py-1 rounded-full border border-slate-300"
                  >
                    {l.name}
                    <button
                      type="button"
                      onClick={() => removeLabel(l.groupResourceName)}
                      className="hover:text-slate-900"
                      aria-label={`Remove label ${l.name}`}
                    >
                      <X size={11} />
                    </button>
                  </span>
                ))}
                <button
                  type="button"
                  onClick={() => setLabelPickerOpen((v) => !v)}
                  className="inline-flex items-center gap-1 text-xs font-semibold text-slate-800 hover:text-slate-900 border border-dashed border-slate-300 hover:border-slate-500 rounded-full px-2 py-1"
                >
                  <Plus size={11} />
                  Add label
                </button>
              </div>

              {labelPickerOpen && (
                <div className="mt-2 border border-slate-200 rounded-md bg-white shadow-sm p-2 space-y-1 max-h-[240px] overflow-y-auto">
                  <input
                    value={labelSearch}
                    onChange={(e) => setLabelSearch(e.target.value)}
                    placeholder="Search labels…"
                    autoFocus
                    className="w-full border border-slate-300 rounded px-2 py-1.5 text-sm mb-1 outline-none focus:border-slate-500"
                  />
                  {availableGroups.length === 0 && (
                    <p className="text-xs text-slate-500 px-1 py-1">
                      No labels synced yet — create one below or sync from
                      Google.
                    </p>
                  )}
                  {availableGroups.length > 0 &&
                    availableGroups.filter(
                      (g) =>
                        !labelIsAttached(g) &&
                        g.name
                          .toLowerCase()
                          .includes(labelSearch.trim().toLowerCase()),
                    ).length === 0 && (
                      <p className="text-xs text-slate-400 px-1 py-1">
                        No labels match "{labelSearch}".
                      </p>
                    )}
                  {availableGroups
                    .filter(
                      (g) =>
                        !labelIsAttached(g) &&
                        g.name
                          .toLowerCase()
                          .includes(labelSearch.trim().toLowerCase()),
                    )
                    .map((g) => (
                      <button
                        key={g.resourceName}
                        type="button"
                        onClick={() => attachLabel(g)}
                        className="w-full text-left px-2 py-1 text-sm rounded hover:bg-slate-50 flex items-center justify-between"
                      >
                        <span>{g.name}</span>
                        <span className="text-[10px] text-slate-400 uppercase tracking-wider">
                          {g.source === "system" ? "Google" : ""}
                        </span>
                      </button>
                    ))}
                  <div className="border-t border-slate-200 pt-2 mt-1 flex items-center gap-2">
                    <input
                      value={labelCreateName}
                      onChange={(e) => setLabelCreateName(e.target.value)}
                      placeholder="New label name"
                      className="flex-1 border border-slate-300 rounded px-2 py-1 text-sm"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleCreateLabel();
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={handleCreateLabel}
                      disabled={!labelCreateName.trim() || creatingLabel}
                      className="px-2 py-1 text-xs font-semibold rounded bg-slate-800 hover:bg-slate-900 text-white disabled:opacity-50"
                    >
                      {creatingLabel ? "…" : "Create"}
                    </button>
                  </div>
                </div>
              )}
            </Section>

            <Section icon={FileText} title="Notes">
              <textarea
                className={`${inputClass} min-h-[100px] resize-y`}
                value={form.notes}
                onChange={(e) => updateField("notes", e.target.value)}
                placeholder="Anything worth remembering about this contact…"
              />
            </Section>
          </div>

          {/* Footer */}
          <div className="border-t border-slate-200 px-5 py-3 flex items-center justify-end gap-2 shrink-0 bg-white">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-sm font-semibold rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 text-sm font-semibold rounded-md bg-slate-800 hover:bg-slate-900 text-white disabled:opacity-50"
            >
              {saving ? "Saving…" : isEdit ? "Save changes" : "Create contact"}
            </button>
          </div>
        </form>
        </div>
      </div>
    </>,
    document.body,
  );
}
